import { IconEye, IconEyeInvisible } from '@arco-design/web-react/icon';
import React, { useCallback } from 'react';
import { Tooltip, useBlock } from '@bay-email/editor';
import {
	BasicType,
	BlockManager,
	isDynamicListingColumn,
	t,
} from '@bay-email/core';
import { BsChevronBarContract } from 'react-icons/bs';
import { useTruncateCheck } from '@bay-ui/utilities/hook';

const FOOTER_BLOCK_CLASS = 'bay-email-footer-block';

function isFooterBlock(blockData: any) {
	if (!blockData) return false;
	if (blockData.type !== BasicType.TEXT && blockData.type !== 'advanced_text') {
		return false;
	}

	const isFooterFlag = Boolean(blockData?.data?.value?.isFooterBlock);
	const cssClass = String(blockData?.attributes?.['css-class'] || '');
	const content = String(blockData?.data?.value?.content || '').toLowerCase();

	return (
		isFooterFlag ||
		cssClass.includes(FOOTER_BLOCK_CLASS) ||
		(content.includes('equal housing opportunity') &&
			content.includes('unsubscribe'))
	);
}

export interface AttributesPanelWrapper {
	style?: React.CSSProperties;
	extra?: React.ReactNode;
	children: React.ReactNode | React.ReactElement;
	onCollapseAll?: () => void;
	allCollapsed?: boolean;
}
export const AttributesPanelWrapper: React.FC<AttributesPanelWrapper> = props => {
	const { focusBlock, setFocusBlock } = useBlock();
	const block = focusBlock && BlockManager.getBlockByType(focusBlock.type);

	const onChangeHidden = useCallback(
		(val: string | boolean) => {
			if (!focusBlock) return;
			focusBlock.data.hidden = val as any;
			setFocusBlock({ ...focusBlock });
		},
		[focusBlock, setFocusBlock],
	);

	// Get custom title for listing blocks
	const getBlockTitle = useCallback(() => {
		if (!block) return '';

		if (isFooterBlock(focusBlock)) {
			return t('Footer Attributes');
		}

		// Custom titles for listing blocks
		if (focusBlock?.type === 'text_single_listing') {
			return t('Single Listing Attributes');
		}
		if (focusBlock?.type === 'listings_block') {
			return t('Multiple Listings Attributes');
		}
		if (focusBlock?.type === 'criteria_block') {
			return t('By Criteria Listings Attributes');
		}
		// A listing column is an ordinary column with a flag, so the block name
		// behind it is still "Column" — name it here instead.
		if (isDynamicListingColumn(focusBlock)) {
			return t('Listing Column Attributes');
		}

		// Default title format
		return `${block.name} ${t('Attributes')}`;
	}, [block, focusBlock]);

	const blockTitle = getBlockTitle();
	const [titleRef, isTitleTruncated] = useTruncateCheck([blockTitle]);

	if (!focusBlock || !block) return null;

	return (
		<div>
			<div
				className='p-3 ps-5 pe-4'
				style={{
					display: 'flex',
					alignItems: 'center',
				}}
			>
				<Tooltip
					disabled={!isTitleTruncated}
					placement='top'
					content={blockTitle}
				>
					<div className='flex-1 text-truncate cursor-pointer fs-4 fw-bold text-900' ref={titleRef}>
						{blockTitle}
					</div>
				</Tooltip>
				
					<span className='m-2'><EyeIcon /></span>
					{props.onCollapseAll && (
						<Tooltip
							placement='top-end'
							content={
								<span style={{ whiteSpace: 'nowrap' }}>
									{props.allCollapsed ? t('Expand All') : t('Collapse All')}
								</span>
							}
						>
							<button
								type='button'
								className='m-2 p-0'
								onClick={props.onCollapseAll}
								style={{
									background: 'none',
									border: 'none',
									cursor: 'pointer',
									fontSize: '14px',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									borderRadius: '4px',
									transition: 'background-color 0.2s',
								}}
								onMouseEnter={e => {
									e.currentTarget.style.backgroundColor = '#e5eefc';
								}}
								onMouseLeave={e => {
									e.currentTarget.style.backgroundColor = 'transparent';
								}}
							>
								{React.createElement(BsChevronBarContract as any)}
							</button>
						</Tooltip>
					)}
					{props.extra}
			</div>

			<div style={{ padding: '0px', ...props.style }}>{props.children}</div>
		</div>
	);
};

function EyeIcon() {
	const { setFocusBlock, focusBlock } = useBlock();

	const onToggleVisible = useCallback(
		(e: React.MouseEvent) => {
			if (!focusBlock) return null;
			e.stopPropagation();
			setFocusBlock({
				...focusBlock,
				data: {
					...focusBlock.data,
					hidden: !focusBlock.data.hidden,
				},
			});
		},
		[focusBlock, setFocusBlock],
	);

	if (!focusBlock) return null;

	if (focusBlock.type === BasicType.PAGE) return null;

	const tooltipText = focusBlock.data.hidden
		? t('Show this block')
		: t('Hide this block');

	return (
		<Tooltip
			placement='left'
			content={tooltipText}
		>
			{focusBlock.data.hidden ? (
				<IconEyeInvisible
					style={{ cursor: 'pointer', fontSize: 16, position: 'relative', top: 1 }}
					onClick={onToggleVisible}
				/>
			) : (
				<IconEye
					style={{ cursor: 'pointer', fontSize: 16, position: 'relative', top: 1 }}
					onClick={onToggleVisible}
				/>
			)}
		</Tooltip>
	);
}
